import * as React from 'react';

import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';
import type {User} from 'nutshell-core/users/users-types';

import type {WidgetFragment as Widget, Lead} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {CollapsibleCardWidget} from '../../core/card/collapsible-card-widget';
import {AiSummaryWidget} from '../../lead/content/ai-summary-widget/ai-summary-widget';
import {LeadEmailSequencesWidget} from '../../lead/content/automated-emails-widget/lead-email-sequences-widget';
import {OldSalesProcessWidget} from '../../lead/content/sales-process-widget/old-sales-process/old-sales-process-widget';
import {SalesProcessWidget} from '../../lead/content/sales-process-widget/sales-process-widget';
import {LeadTasksWidget} from '../../lead/content/tasks-widget/lead-tasks-widget';
import {AccountsAndContactsWidgetWrapper} from '../../lead/sidebar/accounts-and-contacts-widget/accounts-and-contacts-widget-wrapper';
import {LeadAttachmentsWidget} from '../../lead/sidebar/attachment-widget/components/lead-attachments-widget';
import {CompetitorsWidgetWrapper} from '../../lead/sidebar/competitors-widget/competitors-widget-wrapper';
import {LeadCustomFieldsWidget} from '../../lead/sidebar/custom-fields-widget/lead-custom-fields-widget';
import {LeadResearcherWidget} from '../../lead/sidebar/lead-researcher-widget/lead-researcher-widget';
import {ProductWidgetWrapper} from '../../lead/sidebar/product-widget/product-widget-wrapper';
import {RevenueWidget} from '../../lead/sidebar/revenue-widget/components/revenue-widget';
import {SourcesWidgetWrapper} from '../../lead/sidebar/source-widget/sources-widget-wrapper';
import {LeadSummaryWidget} from '../../lead/sidebar/summary-widget/lead-summary-widget';
import {AccountEmailSequencesWidget} from '../../peep/account/content/email-sequences-widget/account-email-sequences-widget';
import {RelatedContactsWidget} from '../../peep/account/content/related-contacts-widget/related-contacts-widget';
import {AccountHierarchyWidget} from '../../peep/account/sidebar/account-hierarchy/account-hierarchy-widget';
import {AccountAttachmentsWidget} from '../../peep/account/sidebar/attachments-widget/components/account-attachments-widget';
import {AccountAllCustomFieldsWidget} from '../../peep/account/sidebar/custom-fields-widget/account-all-custom-fields-widget';
import {AccountCustomFieldsWidget} from '../../peep/account/sidebar/custom-fields-widget/account-custom-fields-widget';
import {AccountSummaryWidget} from '../../peep/account/sidebar/summary-widget/account-summary-widget';
import {ContactEmailSequencesWidget} from '../../peep/contact/content/email-sequences-widget/contact-email-sequences-widget';
import {RelatedAccountsWidget} from '../../peep/contact/content/related-accounts-widget/related-accounts-widget';
import {ContactAttachmentsWidget} from '../../peep/contact/sidebar/attachments-widget/components/contact-attachments-widget';
import {AudiencesWidget} from '../../peep/contact/sidebar/audiences-widget/audiences-widget';
import {ContactAllCustomFieldsWidget} from '../../peep/contact/sidebar/custom-fields-widget/contact-all-custom-fields-widget';
import {ContactCustomFieldsWidget} from '../../peep/contact/sidebar/custom-fields-widget/contact-custom-fields-widget';
import {FormSubmissionsWidget} from '../../peep/contact/sidebar/form-submissions-widget/form-submissions-widget';
import {ContactSummaryWidget} from '../../peep/contact/sidebar/summary-widget/contact-summary-widget';
import {BusinessCardWidget} from '../../peep/shared/business-card-widget/business-card-widget';
import {EmailEngagementWidget} from '../../peep/shared/email-engagement-widget/email-engagement-widget';
import {ChildAccountLeadsWidget} from '../../peep/shared/leads-widget/child-account-leads-widget';
import {PeepLeadsWidget} from '../../peep/shared/leads-widget/leads-widget';
import {PeepTasksWidget} from '../../peep/shared/tasks-widget/peep-tasks-widget';

import {ActivitiesWidget} from '../widgets/activities-widget/components/activities-widget';

type LeadWidgetOptions = {
    mode: 'lead';
    widgets: Widget[];
    lead: Lead;
    user: User;
};

type PeepWidgetOptions = {
    mode: 'account' | 'contact';
    entityId: string;
    widgets: Widget[];
    user: User;
    fallbackLabel?: (type: Widget['type'], name?: string | null) => string;
};

type WidgetOptions = LeadWidgetOptions | PeepWidgetOptions;

export function getCustomFieldAddOptions(user?: User | null, stagesetId?: string | null) {
    const canManageCustomFields = user && user.permissions.canAccessSetup;

    if (!canManageCustomFields) {
        return undefined;
    }

    return {
        label: 'Manage',
        variant: 'singular',
        onClick: () => {
            if (stagesetId) {
                appHistory.push(`/setup/custom-field/lead?pipeline=${stagesetId}`);
            } else {
                appHistory.push('/setup/custom-field/lead');
            }
        },
    };
}

function getDefaultFallbackLabel(type: Widget['type']): string {
    switch (type) {
        case 'ALL_CUSTOM_FIELDS':
        case 'CUSTOM_CUSTOM_FIELDS':
            return 'Custom fields';
        case 'AUTOMATED_EMAILS':
            return 'Automated emails';
        case 'TASKS':
            return 'Tasks';
        case 'ACTIVITIES':
            return 'Activities';
        case 'RELATED_CONTACTS':
            return 'People';
        case 'RELATED_ACCOUNTS':
            return 'Companies';
        case 'COMPANY_HIERARCHY':
            return 'Company hierarchy';
        case 'EMAIL_ENGAGEMENT':
            return 'Email engagement';
        case 'BUSINESS_CARD':
            return 'Business card';
        case 'LEADS':
            return 'Leads';
        case 'AUDIENCES':
            return 'Audiences';
        case 'FORMS':
            return 'Forms';
        case 'ATTACHMENTS':
            return 'Attachments';
        case 'COMPANIES_AND_PEOPLE':
            return 'Companies and people';
        case 'REVENUE':
            return 'Revenue';
        case 'SOURCES':
            return 'Sources';
        case 'COMPETITORS':
            return 'Competitors';
        case 'SALES_PROCESS':
            return 'Sales process';
        case 'AI_SUMMARY':
            return 'AI summary';
        case 'LEAD_RESEARCHER':
            return 'Lead researcher';
        case 'CHILD_LEADS':
            return 'Child leads';
        case 'SUMMARY':
            return 'Summary';
        default:
            exhaustivenessCheck(type);
    }
}

// TODO: Remove this once we build out widgets for peep pages
function getPlaceholderWidgetComponent(
    widget: Widget,
    fallbackLabel?: (type: Widget['type'], name?: string | null) => string
) {
    const name =
        widget.name ??
        fallbackLabel?.(widget.type, widget.name) ??
        getDefaultFallbackLabel(widget.type);
    const widgetWithFallbackName = {...widget, name};

    return (
        <CollapsibleCardWidget
            key={widget.id}
            widget={widgetWithFallbackName}
            hasBorder={true}
            children={<div />}
        />
    );
}

export function getWidgetFallbackLabel(type: Widget['type'], name?: string | null): string {
    return name ?? getDefaultFallbackLabel(type);
}

export function getWidgetComponents(options: WidgetOptions): React.ReactNode[] {
    return options.widgets
        .filter((widget) => !widget.isHidden && !widget.isDisabledByAdmin)
        .map((widget) => {
            switch (widget.type) {
                case 'COMPANIES_AND_PEOPLE':
                    if (options.mode === 'lead') {
                        return (
                            <AccountsAndContactsWidgetWrapper
                                key={widget.id}
                                leadId={options.lead.id}
                                widget={widget}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'REVENUE':
                    if (options.mode === 'lead') {
                        if (NutClientConfig.hasRevenueWidgetFF) {
                            return (
                                <RevenueWidget
                                    key={widget.id}
                                    lead={options.lead}
                                    widget={widget}
                                    canUseSalesDocuments={
                                        options.user.permissions.canUseSalesDocuments
                                    }
                                />
                            );
                        }

                        return (
                            <ProductWidgetWrapper
                                key={widget.id}
                                leadId={options.lead.id}
                                widget={widget}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'SOURCES':
                    if (options.mode === 'lead') {
                        return (
                            <SourcesWidgetWrapper
                                key={widget.id}
                                leadId={options.lead.id}
                                user={options.user}
                                widget={widget}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'ALL_CUSTOM_FIELDS':
                    if (options.mode === 'lead') {
                        return (
                            <LeadCustomFieldsWidget
                                key={widget.id}
                                leadId={options.lead.id}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'account') {
                        return (
                            <AccountAllCustomFieldsWidget
                                key={widget.id}
                                accountId={options.entityId}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'contact') {
                        return (
                            <ContactAllCustomFieldsWidget
                                key={widget.id}
                                contactId={options.entityId}
                                widget={widget}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'CUSTOM_CUSTOM_FIELDS':
                    if (options.mode === 'lead') {
                        return (
                            <LeadCustomFieldsWidget
                                key={widget.id}
                                leadId={options.lead.id}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'account') {
                        return (
                            <AccountCustomFieldsWidget
                                key={widget.id}
                                accountId={options.entityId}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'contact') {
                        return (
                            <ContactCustomFieldsWidget
                                key={widget.id}
                                contactId={options.entityId}
                                widget={widget}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'COMPETITORS':
                    if (options.mode === 'lead') {
                        return (
                            <CompetitorsWidgetWrapper
                                key={widget.id}
                                leadId={options.lead.id}
                                widget={widget}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'ATTACHMENTS':
                    if (options.mode === 'lead') {
                        return (
                            <LeadAttachmentsWidget
                                key={widget.id}
                                leadId={options.lead.id}
                                user={options.user}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'account') {
                        return (
                            <AccountAttachmentsWidget
                                key={widget.id}
                                accountId={options.entityId}
                                user={options.user}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'contact') {
                        return (
                            <ContactAttachmentsWidget
                                key={widget.id}
                                contactId={options.entityId}
                                user={options.user}
                                widget={widget}
                            />
                        );
                    }

                    break;
                case 'TASKS':
                    if (options.mode === 'lead') {
                        return (
                            <LeadTasksWidget
                                key={widget.id}
                                leadId={options.lead.id}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'account' || options.mode === 'contact') {
                        return (
                            <PeepTasksWidget
                                key={widget.id}
                                peepId={options.entityId}
                                widget={widget}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'ACTIVITIES':
                    if (options.mode === 'lead') {
                        return (
                            <ActivitiesWidget
                                key={widget.id}
                                entityId={options.lead.id}
                                widget={widget}
                            />
                        );
                    }

                    return (
                        <ActivitiesWidget
                            key={widget.id}
                            entityId={options.entityId}
                            widget={widget}
                        />
                    );
                case 'AUTOMATED_EMAILS':
                    if (options.mode === 'lead') {
                        return (
                            <LeadEmailSequencesWidget
                                key={widget.id}
                                leadId={options.lead.id}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'contact') {
                        return (
                            <ContactEmailSequencesWidget
                                key={widget.id}
                                contactId={options.entityId}
                                widget={widget}
                            />
                        );
                    } else if (options.mode === 'account') {
                        return (
                            <AccountEmailSequencesWidget
                                key={widget.id}
                                accountId={options.entityId}
                                widget={widget}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'SALES_PROCESS':
                    if (options.mode === 'lead') {
                        if (NutClientConfig.hasSalesProcessSetupOverhaulNUT7581) {
                            if (!options.lead.stages.current) {
                                return null;
                            }

                            return (
                                <SalesProcessWidget
                                    key={widget.id}
                                    widget={widget}
                                    leadId={options.lead.id}
                                    leadStageEdges={options.lead.stages.edges}
                                    currentStageId={options.lead.stages.current.id}
                                />
                            );
                        }

                        return <OldSalesProcessWidget key={widget.id} leadId={options.lead.id} />;
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'AI_SUMMARY':
                    if (options.mode === 'lead') {
                        return (
                            <AiSummaryWidget
                                key={widget.id}
                                widget={widget}
                                leadId={options.lead.id}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'LEAD_RESEARCHER':
                    if (options.mode === 'lead') {
                        return (
                            <LeadResearcherWidget
                                key={widget.id}
                                widget={widget}
                                leadId={options.lead.id}
                                canResearch={options.lead.research.canResearch}
                                existingResearch={options.lead.research.summary}
                            />
                        );
                    }

                    return getPlaceholderWidgetComponent(widget, options.fallbackLabel);
                case 'RELATED_CONTACTS':
                    if (options.mode === 'account') {
                        return (
                            <RelatedContactsWidget
                                key={widget.id}
                                widget={widget}
                                accountId={options.entityId}
                            />
                        );
                    }

                    return null;
                case 'RELATED_ACCOUNTS':
                    if (options.mode === 'contact') {
                        return (
                            <RelatedAccountsWidget
                                key={widget.id}
                                widget={widget}
                                contactId={options.entityId}
                            />
                        );
                    }

                    return null;
                case 'BUSINESS_CARD':
                    if (options.mode === 'account' || options.mode === 'contact') {
                        return (
                            <BusinessCardWidget
                                key={widget.id}
                                widget={widget}
                                entityId={options.entityId}
                                peepType={options.mode}
                            />
                        );
                    }

                    break;
                case 'COMPANY_HIERARCHY':
                    if (options.mode === 'account') {
                        return (
                            <AccountHierarchyWidget
                                key={widget.id}
                                widget={widget}
                                accountId={options.entityId}
                            />
                        );
                    }

                    return null;
                case 'FORMS':
                    if (options.mode === 'contact') {
                        return (
                            <FormSubmissionsWidget
                                key={widget.id}
                                widget={widget}
                                contactId={options.entityId}
                            />
                        );
                    }

                    break;
                case 'LEADS':
                    if (options.mode === 'account' || options.mode === 'contact') {
                        return <PeepLeadsWidget peepId={options.entityId} widget={widget} />;
                    }

                    return null;
                case 'AUDIENCES':
                    if (options.mode === 'contact') {
                        return (
                            <AudiencesWidget
                                key={widget.id}
                                widget={widget}
                                contactId={options.entityId}
                                user={options.user}
                            />
                        );
                    }

                    break;
                case 'EMAIL_ENGAGEMENT':
                    if (options.mode === 'account' || options.mode === 'contact') {
                        return <EmailEngagementWidget peepId={options.entityId} widget={widget} />;
                    }

                    return null;

                case 'CHILD_LEADS':
                    if (options.mode === 'account') {
                        return (
                            <ChildAccountLeadsWidget
                                key={widget.id}
                                accountId={options.entityId}
                                widget={widget}
                            />
                        );
                    }

                    return null;
                case 'SUMMARY':
                    if (options.mode === 'account') {
                        return (
                            <AccountSummaryWidget
                                accountId={options.entityId}
                                widget={widget}
                                user={options.user}
                            />
                        );
                    } else if (options.mode === 'contact') {
                        return (
                            <ContactSummaryWidget
                                contactId={options.entityId}
                                widget={widget}
                                user={options.user}
                            />
                        );
                    } else if (options.mode === 'lead') {
                        return (
                            <LeadSummaryWidget
                                key={widget.id}
                                lead={options.lead}
                                widget={widget}
                                user={options.user}
                            />
                        );
                    }

                    break;
                default:
                    exhaustivenessCheck(widget.type);
            }
        });
}
